import React, { useState, useMemo, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  ESSENTIALDETAIL,
  ROOMESSENTAILADD,
  ROOMESSENTIALUPDATE,
} from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import fileUploader from "../../../../utilities/fileUploader";
import Loading from "../../../../components/Common/Loading";

const getSchema = () =>
  z.object({
    imgUrl: z.string({ required_error: "Image is required" }),
    name: z.string({ required_error: "Amenity name is required" }),
    description: z.string().min(1, "Description is required"),
    status: z.boolean().optional(),
  });

const AddRoomAmenity = () => {
  const [promoCodeDetails, setPromocodeDetails] = useState(null);
  const { id } = useParams();
  const schema = useMemo(() => getSchema(id), [id]);
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [imgloader, setImgLoader] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      imgUrl: "",
      name: "",
      description: "",
      status: "",
    },
  });

  const submitHandler = catchAsync(async (data) => {
    console.log(data, "data=====<>");
    const body = {
      ...data,
      ...(id && { _id: id }),
      status: data.status ? "Active" : "Inactive",
    };
    

    
    const res = await (id ? ROOMESSENTIALUPDATE(body) : ROOMESSENTAILADD(body));
    const success = checkResponse({ res, showSuccess: true });

    if (success) {
      navigate("/roomamenity");
    }
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await ESSENTIALDETAIL(id);
    const success = checkResponse({
      res,
      setData: setPromocodeDetails,
      setLoader,
    });
    if (success && res?.data?.data) {
      const fetched = res.data.data;
      reset({
        ...fetched,
        status: fetched.status === "Active",
      });
      setImagePreview(fetched.imgUrl);
    }
  }, setLoader);

  useEffect(() => {
    if (id) getData();
  }, [id]);

  const handleCancel = () => {
    navigate("/amenities");
  };

  const handleImageChange = catchAsync(async (e) => {
    setImgLoader(true);
    const file = e.target.files[0];
    const res = await fileUploader(file);

    setValue("imgUrl", res?.url);
    setImgLoader(false);

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  });

  return (
    <>
      {loader && <Loader />}
      <section className="addPromo position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to="/roomamenity"
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? "Edit Room Amenities" : "Add Room Amenities"}
                </h4>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="5" md="6" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Amenities Image:
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handleImageChange}
                        />
                        {errors?.imgUrl && (
                          <p className="text-danger m-0">
                            {errors?.imgUrl?.message}
                          </p>
                        )}
                        {imgloader ? (
                          <div
                            style={{ height: 140, width: 140 }}
                            className="img-fluid rounded-circle object-fit-cover"
                          >
                            <Loading fullSize={true} />
                          </div>
                        ) : (
                          imagePreview && (
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{ width: "100%", marginTop: "10px" }}
                            />
                          )
                        )}
                      </div>
                    </Col>

                    <Col lg="5" md="6" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Amenities name:
                        </label>
                        <input
                          type="text"
                          placeholder="Amenity Name"
                          className="form-control"
                          {...register("name")}
                        />
                        {errors?.name && (
                          <p className="text-danger m-0">
                            {errors?.name?.message}
                          </p>
                        )}
                      </div>
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Status
                        </label>
                        <select
                          className="form-control"
                          {...register("status", {
                            setValueAs: (val) => val === "Active",
                          })}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                        {errors?.status && (
                          <p className="text-danger m-0">
                            {errors?.status?.message}
                          </p>
                        )}
                      </div>

                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Description
                        </label>
                        <textarea
                          rows={5}
                          className="form-control h-auto"
                          {...register("description")}
                        ></textarea>
                        {errors?.description && (
                          <p className="text-danger m-0">
                            {errors?.description?.message}
                          </p>
                        )}
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="d-flex align-items-center justify-content-center commonBtn GreyBtn"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="d-flex align-items-center justify-content-center commonBtn "
                        >
                          Submit
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddRoomAmenity;
